import {
    useQueryClient,
    useMutation,
    QueryClient,
    UseMutationResult,
} from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { BudgetExerciceDisplaySettings } from '../types';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { updateBudgetProposalDisplayPositionQueryData } from '../helper';

/**
 * A mutation hook for updating user budget display settings.
 *
 * Provides a flexible mutation handler with customizable `onSuccess`
 * and `onError` callbacks.
 */
const useBudgetDisplaySettingsMutation = ({
    onSuccess,
    onError,
}: {
    onSuccess?: (
        response: BudgetExerciceDisplaySettings[],
        newValues: string[],
        queryClient: QueryClient,
    ) => void;
    onError?: (
        prevValues: Record<string, number[]>,
        queryClient: QueryClient,
    ) => void;
}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            body,
        }: {
            body: BudgetExerciceDisplaySettings[];
            newValues?: string[];
            prevValues?: Record<string, number[]>;
        }) =>
            RQAPICallWithData<BudgetExerciceDisplaySettings[]>({
                endpoint: 'budget_user_settings/exercice/budget_display',
                method: 'PATCH',
                body,
            }),

        onSuccess: (response, { newValues }) => {
            if (newValues)
                onSuccess?.(response.content.data, newValues, queryClient);
        },

        onError: (_, { prevValues }) => {
            if (prevValues) onError?.(prevValues, queryClient);
        },
    });
};

/**
 * Hook to update the budget display settings and refresh related cache data on success.
 */
export const useUpdateBudgetDisplaySettings = (
    setBpSoftwareIds: DispatchState<string[]>,
): UseMutationResult =>
    useBudgetDisplaySettingsMutation({
        onSuccess: (
            responseData: BudgetExerciceDisplaySettings[],
            newValues: string[],
            queryClient: QueryClient,
        ) => {
            const { budgetProposalsKeys } = createBudgetQueryKeys();
            queryClient.setQueryData(
                budgetProposalsKeys(),
                (budgetProposals: BudgetProposalT[]) =>
                    budgetProposals.map((bp) => {
                        const match = responseData.find(
                            (d) =>
                                d.exercice === bp.exercice &&
                                d.budgetSoftwareId === bp.budgetSoftwareId,
                        );
                        return {
                            ...bp,
                            displayPosition: match
                                ? match.budgetDisplayPosition
                                : bp.displayPosition,
                        };
                    }),
            );
            setBpSoftwareIds(newValues);
        },
    });

/**
 * Hook to update budget display settings with rollback on error.
 * If the mutation fails, it restores the previous display settings
 * and updates the query cache accordingly.
 */
export const useUpdateBudgetDisplaySettingsWithRollback = (
    setSectionBpIdsMap: DispatchState<Record<string, number[]>>,
) =>
    useBudgetDisplaySettingsMutation({
        onError: (
            prevValues: Record<string, number[]>,
            queryClient: QueryClient,
        ) => {
            setSectionBpIdsMap(prevValues);
            updateBudgetProposalDisplayPositionQueryData(
                prevValues,
                queryClient,
            );
        },
    });
